import { useState, useEffect, useRef } from "react";
import { DealerLayout } from "@/components/shared/DealerLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  getRequiredDocuments, uploadMyDealerDocument,
  getPreApprovedData, updatePreApprovedData, getPreApprovedDocuments,
  uploadPreApprovedDocument,
} from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FileText, Upload, Loader2, Download, CheckCircle2, AlertCircle,
  Wand2, Trash2, CreditCard, Building2, FileCheck, DollarSign, Users,
  ClipboardList, MapPin, Building, Save, Eye, Plus, X, Camera,
  XCircle, Clock,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REQ_ICONS = {
  state_id: CreditCard, ein_documents: Building2, articles_of_incorporation: FileCheck,
  financials: DollarSign, org_chart: Users, business_plan: ClipboardList,
};
const REQ_DESC = {
  state_id: "Upload a clear copy of your valid state-issued ID or driver's license",
  ein_documents: "Upload your IRS EIN confirmation letter or related documentation",
  articles_of_incorporation: "Upload your company's articles of incorporation or business formation documents",
  financials: "Upload a recent bank statement (within last 90 days)",
  org_chart: "Build your organizational chart using our interactive generator",
  business_plan: "Create your business plan using our guided template builder",
};
const PA_ICONS = {
  plaza_photos: Camera, exterior_photos: Camera, interior_photos: Camera,
  loi_lease: FileText, void_check: FileText, w9_form: FileText,
  resale_certificate: FileText, mtc_form: FileText, merchant_agreement: FileText,
  door_application: FileText, retailer_agreement: FileText,
};

const ReviewStatusBadge = ({ doc }) => {
  if (!doc) return null;
  const s = doc.review_status;
  if (s === "approved") return <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-full"><CheckCircle2 className="w-2.5 h-2.5" /> Approved</span>;
  if (s === "rejected") return <span className="inline-flex items-center gap-1 text-[10px] font-medium text-red-700 bg-red-50 px-1.5 py-0.5 rounded-full"><XCircle className="w-2.5 h-2.5" /> Needs Revision</span>;
  return <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full"><Clock className="w-2.5 h-2.5" /> Pending Review</span>;
};

export default function DealerDocuments() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("required");

  // Required docs state
  const [reqData, setReqData] = useState(null);
  const [reqLoading, setReqLoading] = useState(true);
  const [reqUploadingType, setReqUploadingType] = useState(null);
  const reqFileRefs = useRef({});

  // Pre-approved state
  const [paReqs, setPaReqs] = useState(null);
  const [paForm, setPaForm] = useState({ store_sqft: "", rent: "", ssn: "" });
  const [paLoading, setPaLoading] = useState(true);
  const [paSaving, setPaSaving] = useState(false);
  const [paUploadingType, setPaUploadingType] = useState(null);
  const paFileRefs = useRef({});

  const fetchReqDocs = async () => {
    try { setReqData(await getRequiredDocuments()); } catch {}
    setReqLoading(false);
  };
  const fetchPaDocs = async () => {
    try {
      const [data, docs] = await Promise.all([getPreApprovedData(), getPreApprovedDocuments()]);
      setPaForm({ store_sqft: data.store_sqft || "", rent: data.rent || "", ssn: data.ssn || "" });
      setPaReqs(docs);
    } catch {}
    setPaLoading(false);
  };
  useEffect(() => { fetchReqDocs(); fetchPaDocs(); }, []);

  // Required doc handlers
  const handleReqUpload = async (file, docType) => {
    if (!file) return;
    setReqUploadingType(docType);
    try { await uploadMyDealerDocument(file, docType); toast.success("Uploaded"); await fetchReqDocs(); } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    setReqUploadingType(null);
  };

  // Pre-approved handlers
  const handlePaSaveFields = async () => {
    setPaSaving(true);
    try { await updatePreApprovedData(paForm); toast.success("Information saved"); } catch { toast.error("Failed to save"); }
    setPaSaving(false);
  };
  const handlePaUpload = async (file, docType) => {
    if (!file) return;
    setPaUploadingType(docType);
    try { await uploadPreApprovedDocument(file, docType); toast.success("Uploaded"); setPaReqs(await getPreApprovedDocuments().catch(() => paReqs)); } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    setPaUploadingType(null);
  };
  const handlePaMultiUpload = async (files, docType) => {
    setPaUploadingType(docType);
    for (const file of files) { try { await uploadPreApprovedDocument(file, docType); } catch {} }
    toast.success(`${files.length} file(s) uploaded`);
    setPaReqs(await getPreApprovedDocuments().catch(() => paReqs));
    setPaUploadingType(null);
  };

  // ===== Shared DocSlot =====
  const DocSlot = ({ req, uploadingType, onUpload, onMultiUpload, fileRefs }) => {
    const Icon = REQ_ICONS[req.key] || PA_ICONS[req.key] || FileText;
    const isUploaded = req.status === "uploaded";
    const hasGenerator = !!req.generator;
    const hasTemplate = !!req.template;
    const isMulti = !!req.multi;
    const doc = req.document;
    const docs = req.documents || (doc ? [doc] : []);

    return (
      <div className={`p-4 rounded-lg border transition-colors ${isUploaded ? "border-teal/30 bg-teal/[0.02]" : "border-slate-200"}`} data-testid={`doc-slot-${req.key}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${isUploaded ? "bg-teal/10" : "bg-slate-100"}`}>
              <Icon className={`w-4 h-4 ${isUploaded ? "text-teal" : "text-slate-400"}`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-navy text-sm">{req.label}</span>
                {isUploaded ? (
                  <span className="inline-flex items-center gap-1 text-[10px] font-medium text-teal bg-teal/10 px-1.5 py-0.5 rounded-full">
                    <CheckCircle2 className="w-2.5 h-2.5" /> {docs.length > 1 ? `${docs.length} files` : "Uploaded"}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full">
                    <AlertCircle className="w-2.5 h-2.5" /> Required
                  </span>
                )}
                {isUploaded && docs[0] && <ReviewStatusBadge doc={docs[0]} />}
              </div>
              {!isUploaded && REQ_DESC[req.key] && <p className="text-xs text-slate-400 mt-1">{REQ_DESC[req.key]}</p>}
              {isUploaded && !isMulti && docs[0] && (
                <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                  <span className="truncate max-w-[180px]">{docs[0].original_filename}</span>
                  <span>{new Date(docs[0].created_at).toLocaleDateString()}</span>
                </div>
              )}
              {isUploaded && !isMulti && docs[0]?.review_status === "rejected" && docs[0]?.review_notes && (
                <p className="text-xs text-red-500 mt-1 bg-red-50 px-2 py-1 rounded">Admin notes: {docs[0].review_notes}</p>
              )}
              {isMulti && docs.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {docs.map((d) => (
                    <div key={d.id} className="relative group w-20 h-20 rounded-lg overflow-hidden border border-slate-200 bg-slate-50">
                      {d.content_type?.startsWith("image/") ? (
                        <img src={`${API_URL}${d.url}`} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center"><FileText className="w-6 h-6 text-slate-300" /></div>
                      )}
                      {d.review_status === "approved" && <div className="absolute bottom-0.5 left-0.5 w-4 h-4 bg-emerald-500 text-white rounded-full flex items-center justify-center"><CheckCircle2 className="w-3 h-3" /></div>}
                      {d.review_status === "rejected" && <div className="absolute bottom-0.5 left-0.5 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center"><AlertCircle className="w-3 h-3" /></div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {isUploaded && !isMulti && docs[0] && (
              <a href={`${API_URL}${docs[0].url}`} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm"><Eye className="w-3.5 h-3.5" /></Button>
              </a>
            )}
            {hasTemplate && (
              <a href={`${API_URL}/api/templates/${req.template}`} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm"><Download className="w-3.5 h-3.5 mr-1" /> Template</Button>
              </a>
            )}
            {hasGenerator && !isUploaded && (
              <Button variant="default" size="sm" className="bg-navy hover:bg-navy/90 text-white"
                onClick={() => navigate(req.generator)} data-testid={`doc-generate-${req.key}`}>
                <Wand2 className="w-3.5 h-3.5 mr-1" /> Generate
              </Button>
            )}
            {!isUploaded && (
              <>
                <input type="file" className="hidden"
                  ref={(el) => (fileRefs.current[req.key] = el)} multiple={isMulti}
                  accept={isMulti ? "image/*,.pdf" : req.key === "state_id" ? "image/*,.pdf" : ".pdf,.doc,.docx,.png,.jpg,.jpeg"}
                  onChange={(e) => {
                    if (isMulti && e.target.files.length > 0) onMultiUpload?.(Array.from(e.target.files), req.key);
                    else if (e.target.files[0]) onUpload(e.target.files[0], req.key);
                    e.target.value = "";
                  }} />
                <Button
                  variant={!hasTemplate && !hasGenerator ? "default" : "outline"}
                  size="sm"
                  className={!hasTemplate && !hasGenerator ? "bg-teal hover:bg-teal-dark text-white" : ""}
                  onClick={() => fileRefs.current[req.key]?.click()} disabled={uploadingType === req.key}
                  data-testid={`doc-upload-${req.key}`}>
                  {uploadingType === req.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (
                    <>{isMulti ? <Plus className="w-3.5 h-3.5 mr-1" /> : <Upload className="w-3.5 h-3.5 mr-1" />} {isMulti ? "Add" : "Upload"}</>
                  )}
                </Button>
              </>
            )}
            {isUploaded && isMulti && (
              <>
                <input type="file" className="hidden"
                  ref={(el) => (fileRefs.current[req.key] = el)} multiple={true}
                  accept="image/*,.pdf"
                  onChange={(e) => {
                    if (e.target.files.length > 0) onMultiUpload?.(Array.from(e.target.files), req.key);
                    e.target.value = "";
                  }} />
                <Button variant="outline" size="sm"
                  onClick={() => fileRefs.current[req.key]?.click()} disabled={uploadingType === req.key}
                  data-testid={`doc-upload-${req.key}`}>
                  {uploadingType === req.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Plus className="w-3.5 h-3.5 mr-1" /> Add</>}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const ProgressBar = ({ completed, total, color = "bg-navy" }) => {
    const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
    return (
      <Card className="border-slate-200 mb-4">
        <CardContent className="p-3">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-medium text-navy">Progress</span>
            <span className="text-xs font-semibold text-slate-600">
              {completed >= total ? <span className="text-teal flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Complete</span> : `${completed} / ${total}`}
            </span>
          </div>
          <div className="w-full bg-slate-100 rounded-full h-2">
            <motion.div className={`h-2 rounded-full ${completed >= total ? "bg-teal" : color}`}
              initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.6 }} />
          </div>
        </CardContent>
      </Card>
    );
  };

  const reqReqs = reqData?.requirements || [];
  const reqCompleted = reqData?.uploaded_count || 0;
  const reqTotal = reqData?.total_count || 6;
  const paReqList = paReqs?.requirements || [];
  const locationDocs = paReqList.filter((r) => r.section === "location");
  const vidapayDocs = paReqList.filter((r) => r.section === "vidapay");
  const primeDocs = paReqList.filter((r) => r.section === "prime_media");
  const locationUploaded = locationDocs.filter((r) => r.status === "uploaded").length;
  const vidapayUploaded = vidapayDocs.filter((r) => r.status === "uploaded").length;
  const primeUploaded = primeDocs.filter((r) => r.status === "uploaded").length;

  return (
    <DealerLayout>
      <div className="max-w-4xl mx-auto" data-testid="dealer-documents-page">
        <div className="mb-5">
          <h1 className="font-heading font-bold text-2xl text-navy">Documents</h1>
          <p className="text-slate-500 font-body text-sm mt-0.5">Upload all required documents to complete your application</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-5 flex-wrap h-auto gap-1">
            <TabsTrigger value="required" className="flex items-center gap-1.5 text-xs" data-testid="tab-required">
              <FileText className="w-3.5 h-3.5" /> Required
              {reqCompleted > 0 && <span className="text-[10px] font-bold bg-navy/10 text-navy px-1.5 py-0.5 rounded-full">{reqCompleted}/{reqTotal}</span>}
            </TabsTrigger>
            <TabsTrigger value="location" className="flex items-center gap-1.5 text-xs" data-testid="tab-location">
              <MapPin className="w-3.5 h-3.5" /> Location
              {locationUploaded > 0 && <span className="text-[10px] font-bold bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-full">{locationUploaded}/{locationDocs.length}</span>}
            </TabsTrigger>
            <TabsTrigger value="vidapay" className="flex items-center gap-1.5 text-xs" data-testid="tab-vidapay">
              <CreditCard className="w-3.5 h-3.5" /> VidaPay
              {vidapayUploaded > 0 && <span className="text-[10px] font-bold bg-teal/10 text-teal px-1.5 py-0.5 rounded-full">{vidapayUploaded}/{vidapayDocs.length}</span>}
            </TabsTrigger>
            <TabsTrigger value="prime_media" className="flex items-center gap-1.5 text-xs" data-testid="tab-prime-media">
              <Building className="w-3.5 h-3.5" /> Prime Media
              {primeUploaded > 0 && <span className="text-[10px] font-bold bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded-full">{primeUploaded}/{primeDocs.length}</span>}
            </TabsTrigger>
          </TabsList>

          {/* Tab 1: Required Documents + SSN */}
          <TabsContent value="required">
            {reqLoading ? (
              <div className="text-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal mx-auto" /></div>
            ) : (
              <>
                <ProgressBar completed={reqCompleted} total={reqTotal} />
                {/* SSN field */}
                <Card className="border-slate-200 mb-4">
                  <CardContent className="p-4">
                    <div className="max-w-xs">
                      <Label className="text-xs text-slate-500">Social Security Number</Label>
                      <div className="flex gap-2 mt-1">
                        <Input value={paForm.ssn} onChange={(e) => setPaForm({ ...paForm, ssn: e.target.value })}
                          placeholder="XXX-XX-XXXX" type="password" data-testid="preapproved-ssn" />
                        <Button variant="outline" size="sm" onClick={handlePaSaveFields} disabled={paSaving} data-testid="save-ssn-btn">
                          {paSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <div className="space-y-2">
                  {reqReqs.map((req, i) => (
                    <motion.div key={req.key} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                      <DocSlot req={req} uploadingType={reqUploadingType}
                        onUpload={handleReqUpload} fileRefs={reqFileRefs} />
                    </motion.div>
                  ))}
                </div>
              </>
            )}
          </TabsContent>

          {/* Tab 2: Location Information */}
          <TabsContent value="location">
            {paLoading ? (
              <div className="text-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto" /></div>
            ) : (
              <>
                <ProgressBar completed={locationUploaded} total={locationDocs.length} color="bg-indigo-600" />
                {/* Store SQFT and Rent as separate cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                  <Card className="border-slate-200">
                    <CardContent className="p-4">
                      <Label className="text-xs text-slate-500">Store SQFT</Label>
                      <div className="flex gap-2 mt-1">
                        <Input value={paForm.store_sqft} onChange={(e) => setPaForm({ ...paForm, store_sqft: e.target.value })}
                          placeholder="e.g. 2400 sqft" data-testid="preapproved-sqft" />
                        <Button variant="outline" size="sm" onClick={handlePaSaveFields} disabled={paSaving}>
                          {paSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border-slate-200">
                    <CardContent className="p-4">
                      <Label className="text-xs text-slate-500">Monthly Rent</Label>
                      <div className="flex gap-2 mt-1">
                        <Input value={paForm.rent} onChange={(e) => setPaForm({ ...paForm, rent: e.target.value })}
                          placeholder="e.g. $1,200" data-testid="preapproved-rent" />
                        <Button variant="outline" size="sm" onClick={handlePaSaveFields} disabled={paSaving}>
                          {paSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
                <div className="space-y-2">
                  {locationDocs.map((req) => (
                    <DocSlot key={req.key} req={req} uploadingType={paUploadingType}
                      onUpload={handlePaUpload} onMultiUpload={handlePaMultiUpload} fileRefs={paFileRefs} />
                  ))}
                </div>
              </>
            )}
          </TabsContent>

          {/* Tab 3: VidaPay Documents */}
          <TabsContent value="vidapay">
            {paLoading ? (
              <div className="text-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal mx-auto" /></div>
            ) : (
              <>
                <ProgressBar completed={vidapayUploaded} total={vidapayDocs.length} color="bg-teal" />
                <div className="space-y-2">
                  {vidapayDocs.map((req) => (
                    <DocSlot key={req.key} req={req} uploadingType={paUploadingType}
                      onUpload={handlePaUpload} onMultiUpload={handlePaMultiUpload} fileRefs={paFileRefs} />
                  ))}
                </div>
              </>
            )}
          </TabsContent>

          {/* Tab 4: Prime Media Wireless Documents */}
          <TabsContent value="prime_media">
            {paLoading ? (
              <div className="text-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-navy mx-auto" /></div>
            ) : (
              <>
                <ProgressBar completed={primeUploaded} total={primeDocs.length} color="bg-navy" />
                <div className="space-y-2">
                  {primeDocs.map((req) => (
                    <DocSlot key={req.key} req={req} uploadingType={paUploadingType}
                      onUpload={handlePaUpload} onMultiUpload={handlePaMultiUpload} fileRefs={paFileRefs} />
                  ))}
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DealerLayout>
  );
}
